import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram } from "lucide-react";
import { Logo, GoldButton } from "./Brand";
import { useEnquiry } from "./EnquiryDialog";
import { CONTACT, TRAINERS } from "../lib/data";

const quickLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/courses", label: "Courses" },
  { to: "/payment-options", label: "Payment Options" },
  { to: "/faq", label: "FAQs" },
  { to: "/contact", label: "Contact Us" },
];

const policyLinks = [
  { to: "/terms",   label: "Terms & Conditions" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/cookies", label: "Cookie Policy" },
];

export const Footer = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <footer className="relative bg-navy border-t border-white/10 pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 pb-16">
          <div className="space-y-6">
            <Logo />
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              Professional training in aesthetics designed around you.
            </p>
            <div className="flex gap-3">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-instagram"
                className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center text-white/70 hover:border-gold hover:text-gold transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-white/60 hover:text-gold transition-colors text-sm">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold mb-6">Contact</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3 text-white/60"><Mail size={16} strokeWidth={1.5} className="text-gold" /> {CONTACT.email}</li>
              <li className="flex items-start gap-3 text-white/60" data-testid="footer-phones">
                <Phone size={16} strokeWidth={1.5} className="text-gold mt-0.5 shrink-0" />
                <ul className="space-y-1">
                  {TRAINERS.map((t) => (
                    <li key={t.name} className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-white/45">{t.name.split(" ")[0]}</span>
                      <a
                        href={`tel:${t.phone.replace(/\s/g, "")}`}
                        data-testid={`footer-phone-${t.name.split(" ")[0].toLowerCase()}`}
                        className="text-white/70 hover:text-gold transition-colors"
                      >
                        {t.phone}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="flex items-center gap-3 text-white/60"><MapPin size={16} strokeWidth={1.5} className="text-gold" /> {CONTACT.location}</li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold mb-6">Enquire</h4>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">Ready to take the next step? Get in touch with our team today.</p>
            <GoldButton onClick={() => openEnquiry()} data-testid="footer-enquire-button">Enquire Now</GoldButton>
          </div>
        </div>

        <div className="hairline mb-8" />

        <p
          className="text-center text-white/55 text-[11px] tracking-[0.2em] uppercase mb-10"
          data-testid="footer-founders"
        >
          Founded by <span className="text-gold">Edwina Evans</span> of{" "}
          <span className="text-gold">Bonds Aesthetics</span> and{" "}
          <span className="text-gold">Charlotte Thompson</span> of{" "}
          <span className="text-gold">CLouise Aesthetics</span>
        </p>

        <p className="font-playfair text-center text-[13vw] leading-none text-white/[0.04] select-none pointer-events-none tracking-tighter -mb-4">
          THE ACADEMY CO.
        </p>

        {/* Policy links (placeholders until final documents supplied) */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-10 mb-6" data-testid="footer-policy-links">
          {policyLinks.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              data-testid={`footer-policy-${p.to.slice(1)}`}
              className="text-white/45 hover:text-gold transition-colors text-[11px] tracking-[0.2em] uppercase"
            >
              {p.label}
            </Link>
          ))}
        </div>

        <div
          className="text-center text-white/40 text-xs leading-relaxed max-w-2xl mx-auto space-y-1"
          data-testid="footer-legal"
        >
          <p>The Aesthetics Academy Co. is a trading name of Bonds Aesthetics Ltd.</p>
          <p>Bonds Aesthetics Ltd is registered in England and Wales. Company No. {CONTACT.companyNumber}.</p>
          <p>Registered Office: {CONTACT.registeredOffice}</p>
          <p className="pt-3 text-white/30">
            &copy; {new Date().getFullYear()} Bonds Aesthetics Ltd trading as The Aesthetics Academy Co. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
